import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import appConfig from 'app-config';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLDriverVehicleUtil } from 'ml-portals-util-react';
import { MLPolicyChangeDataHolderService } from 'ml-services-common-react';
import { MLInsuredCandidatesComponent, MLNameDetailsComponent, MLAlertGroupComponent } from 'ml-components-common-react';

import { useTranslator } from '@jutro/locale';
import metadata from './MLAddVehicleOperatorPopup.metadata.json5';
import messages from './MLAddVehicleOperatorPopup.messages.js';
import styles from './MLAddVehicleOperatorPopup.module.scss';

const MLAddVehicleOperatorPopup = (props) => {
    const {
        submissionVM,
        onCancel,
        onAdd,
        errorTimestamp,
        setErrorTimestamp,
        viewModelService,
        authHeader,
        showProgressDialog,
        EndorsementService
    } = props;

    const isAMP = appConfig.portalCode === 'AMP';
    const translator = useTranslator();

    const pageAction = MLPolicyChangeDataHolderService.getPageAction()
    const VEHICLE_OPERATORS_PATH = 'lobData.personalUmbrella.coverables.vehicleOperators';
    const vehicleOperatorsVM = _.get(submissionVM, VEHICLE_OPERATORS_PATH, []);
    const vehicleOperatorsVal = _.get(submissionVM, `${VEHICLE_OPERATORS_PATH}.value`, []);

    const initSelectedVehicleOperator = () => {
        const index = vehicleOperatorsVal.findIndex((vo) => vo.fixedID === MLPolicyChangeDataHolderService.getDriver().fixedID);
        return vehicleOperatorsVM.getElement(index);
    };

    const [selectedVehicleOperator] = useState(initSelectedVehicleOperator());
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [insuredCandidateSelected, setInsuredCandidateSelected] = useState(false);

    const { onValidate } = useValidation('VehicleOperatorPagePolChange');
    const { refreshData } = useDataRefresh();

    const {
        checkUniqueSelf,
        checkUniqueSpouse,
        getRatedDriverType,
        getFilteredGender
    } = MLDriverVehicleUtil(viewModelService);

    const updateOperator = () => {
        refreshData();
    };

    const getDriversAge = (vehicleOperator) => {
        const dob = MLLocalDateUtil.localDate2Date(vehicleOperator.value.contact.dateOfBirth);
        _.set(vehicleOperator, 'driverAge.value', MLLocalDateUtil.differenceInYears(new Date(), dob));
    };

    let canAddSpouse = true;
    vehicleOperatorsVM.value.forEach((vehicleOperator) => {
        delete vehicleOperator.isSelected;

        if (vehicleOperator.relationshipToInsured === 'spouse') {
            canAddSpouse = false;
        }
    });

    const nbGenderState = _.get(submissionVM, 'baseData.isValidNonBinaryGenderState_Ext.value');

    const addVehicleOperator = () => {
        selectedVehicleOperator.contact.updated.value = true;
        const uniqueSelf = checkUniqueSelf(vehicleOperatorsVal);
        if (!uniqueSelf) {
            const errorMessage = translator(messages.nonUniqueSelf);
            const newAlerts = [...alerts.filter((alert) => {
                return alert.block !== 'nonUniqueSelf' && alert.block !== 'nonUniqueSpouse'
            })];
            newAlerts.push({ type: 'error', message: errorMessage, block: 'nonUniqueSelf' });
            setAlerts(newAlerts);
            return;
        }

        const uniqueSpouse = checkUniqueSpouse(vehicleOperatorsVal);
        if (!uniqueSpouse) {
            const errorMessage = translator(messages.nonUniqueSpouse);
            const newAlerts = [...alerts.filter((alert) => {
                return alert.block !== 'nonUniqueSelf' && alert.block !== 'nonUniqueSpouse'
            })];
            newAlerts.push({ type: 'error', message: errorMessage, block: 'nonUniqueSpouse' });
            setAlerts(newAlerts);
            return;
        }

        const isValid = submissionVM.aspects.valid && submissionVM.aspects.subtreeValid;
        if (!isValid) {
            setShowValidationErrors(true);
            if (setErrorTimestamp && errorTimestamp !== undefined) {
                setErrorTimestamp(errorTimestamp + 1);
            }
            return;
        }

        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saving, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            const existingDrivers = _.get(submissionVM, `${VEHICLE_OPERATORS_PATH}.value`);
            const updatedDrivers = _.get(updatedEndorsementVM, `${VEHICLE_OPERATORS_PATH}.value`);
            _.set(submissionVM, VEHICLE_OPERATORS_PATH, updatedDrivers);

            if (pageAction === 'add') {
                const fixedIds = [];
                existingDrivers.forEach((drv) => fixedIds.push(drv.fixedID));
                let index = updatedDrivers.findIndex((drv) => !fixedIds.includes(drv.fixedID));
                if (index < 0) {
                    index = updatedDrivers.findIndex((drv) => selectedVehicleOperator.fixedID.value === drv.fixedID);
                }
                const newSelectedVehicleOperator = _.get(updatedEndorsementVM, `${VEHICLE_OPERATORS_PATH}`).getElement(index);

                newSelectedVehicleOperator.value.isSelected = true;
                newSelectedVehicleOperator.value.isToRemove = false;
                newSelectedVehicleOperator.value.canAddSpouse = canAddSpouse;
                newSelectedVehicleOperator.value.contact.isDateOfBirthRequired = true;
                MLPolicyChangeDataHolderService.setDriver(newSelectedVehicleOperator.value);
            } else {
                const index = updatedDrivers.findIndex((drv) => selectedVehicleOperator.fixedID.value === drv.fixedID);
                const newSelectedVehicleOperator = _.get(updatedEndorsementVM, `${VEHICLE_OPERATORS_PATH}`).getElement(index);
                MLPolicyChangeDataHolderService.setDriver(newSelectedVehicleOperator.value);
            }

            onAdd();
            onCancel();
        });
    };

    const overrides = {
        alertComponent: {
            customAlerts: alerts,
            isPOPUP: true
        },
        contentContainer: {
            className: isAMP ? 'ml-add-driver-popup amp-form-padding' : 'ml-add-driver-popup'
        },
        insuredCandidatesComponent: {
            visible: pageAction === 'add',
            submissionVM,
            vehicleOperatorVM: selectedVehicleOperator,
            driverType: getRatedDriverType(),
            setInsuredCandidateSelected,
            viewModelService
        },
        formFieldsContainer: {
            visible: insuredCandidateSelected || pageAction !== 'add'
        },
        nameDetailsComponent: {
            layout: isAMP ? 'ML-Split' : 'ML-Name-Default',
            showLabels: isAMP,
            personVM: selectedVehicleOperator.contact,
            onValidate,
            showValidationErrors,
            onModelChange: updateOperator
        },
        dob: {
            path: "contact.dateOfBirth",
            onBlur: () => {
                getDriversAge(selectedVehicleOperator);
            }
        },
        dobControl: {
            className: isAMP ? "row col-md-7" : "row col-md-6"
        },
        dobTooltipContainer: {
            visible: isAMP
        },
        genderRow: {
            path: "contact.gender",
            availableValues: getFilteredGender(nbGenderState)
        },
        maritalStatus: {
            tooltip: isAMP ? '' : {
                title: translator(messages.maritalStatusTitle),
                text: translator(messages.maritalStatus),
                placement: "bottom"
            }
        },
        maritialStatusTooltipContainer: {
            visible: isAMP
        },
        ratedDriversAgeFirstLicensed: {
            path: "ageFirstLicensed",
            className: styles.ratedDriversAgeFirstLicensed,
            visible: true
        },
        footer: {
            className: isAMP ? 'footer amp-footer-padding col-md-12' : 'footer col-md-12'
        },
        actionBtn: {
            content: pageAction === 'add' ? messages.addDriver : messages.editDriver
        }
    };

    const resolvers = {
        resolveComponentMap: {
            mlInsuredCandidatesComponent: MLInsuredCandidatesComponent,
            mlnamedetailscomponent: MLNameDetailsComponent,
            MLAlertGroupComponent
        },
        resolveCallbackMap: {
            addDriver: addVehicleOperator,
            onCancel
        },
        resolveClassNameMap: styles
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={selectedVehicleOperator}
            onModelChange={updateOperator}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValidationChange={onValidate}
            showErrors={showValidationErrors}
        />
    );
};

MLAddVehicleOperatorPopup.propTypes = {
    submissionVM: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
};

export default MLAddVehicleOperatorPopup;
